/**
 * Main Application Component
 * Handles client-side routing, layout rendering, and global event logging.
 */

import './app.scss';
import { Suspense, useEffect, useRef } from 'react';
import { useLocation, Route, Routes } from 'react-router-dom';
import { Box } from '@mui/material';

import { useConfig } from './context/ConfigContext';
import { Pages } from './config/data/Constants';
import { siteManifest } from './config/navigation';
import { logEvent } from './config/data';
import Loader from './components/loader/Loader';

interface SiteManifestEntry {
	path: string;
	urlKey?: string;
	element?: React.ReactNode;
	index?: boolean;
	children?: SiteManifestEntry[];
}

/** Reset window scroll on pathname change so SPA navigations land at the top. */
const ScrollToTop: React.FC = () => {
	const { pathname } = useLocation();

	useEffect(() => {
		window.scrollTo({ top: 0, left: 0, behavior: 'auto' });
	}, [pathname]);

	return null;
};

// Component: Logs route changes to Firebase Analytics
const RouteChangeLogger: React.FC = () => {
	const location = useLocation();
	const previousPath = useRef('');

	useEffect(() => {
		if (previousPath.current !== location.pathname) {
			previousPath.current = location.pathname;
			logEvent(`Mapsd to ${location.pathname}`).catch(console.error);
		}
	}, [location]);

	return null;
};

function App(): React.JSX.Element {
	const config = useConfig();

	// Recursively renders routes from the site manifest
	const renderRoutes = (manifest: SiteManifestEntry[]): React.ReactNode[] => {
		return manifest
			.filter((page) => {
				return !(page.urlKey === Pages.registerMember && !config.MEMBER_ONBOARDING_PAGE_ENABLED);
			})
			.map((page) => {
				if (page.children) {
					return (
						<Route key={page.path} path={page.path} element={page.element}>
							{renderRoutes(page.children)}
						</Route>
					);
				}
				return <Route key={page.path} path={page.path} element={page.element} index={page.index || undefined} />;
			});
	};

	return (
		<Box sx={{ width: '100%', margin: 0, padding: 0, minHeight: '100vh', display: 'flex', flexDirection: 'column' }}>
			<ScrollToTop />
			{Boolean(config.ROUTE_LOGGING_ENABLED) && <RouteChangeLogger />}
			<Suspense fallback={<Loader />}>
				<Routes>{renderRoutes(siteManifest)}</Routes>
			</Suspense>
		</Box>
	);
}

export default App;
